import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Copy, Trash2, Check } from "lucide-react";

/**
 * Prompt 15 §13-17 — Referral Link Generator for collaborators.
 * Admin can create collaborators (e.g. Geoff) and copy their referral URL.
 * The URL is auto-generated: /?utm_source=collaborator&utm_medium=referral
 * &utm_campaign=<slug>&ref=<slug>
 */
export default function CollaboratorsAdmin() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/collaborators");
      setItems(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    await api.post("/collaborators", { name: name.trim(), active: true, notes: "" });
    setName("");
    await load();
  };

  const toggleActive = async (c) => {
    await api.put(`/collaborators/${c.id}`, { name: c.name, active: !c.active, notes: c.notes || "" });
    await load();
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete collaborator "${c.name}"? Their historical tracking events stay intact.`)) return;
    await api.delete(`/collaborators/${c.id}`);
    await load();
  };

  const copy = async (c) => {
    try {
      await navigator.clipboard.writeText(c.url);
      setCopied(c.id);
      setTimeout(() => setCopied(""), 1500);
    } catch { /* clipboard blocked — fall back to selection */ }
  };

  return (
    <div data-testid="admin-collaborators-page">
      <div className="flex items-baseline justify-between mb-2">
        <h1 className="font-display text-[#F5F1EB] text-3xl">Collaborators</h1>
        <span className="text-[11px] text-[#8A8A8A] uppercase tracking-[0.18em]">
          {items.length} total
        </span>
      </div>
      <p className="text-sm text-[#8A8A8A] mb-6 max-w-3xl">
        Create a referral link for each collaborator (influencer, hotel concierge, driver, etc.).
        The link is auto-generated with UTM parameters. Every visit that lands via a collaborator
        link is attributed to them for the whole session — including the WhatsApp click.
      </p>

      <div className="border border-[#2A2A2A] p-4 mb-6" data-testid="collab-new">
        <div className="text-overline text-[#C9A55A] mb-3">Add collaborator</div>
        <div className="flex gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name (e.g. Geoff)"
            data-testid="collab-name-input"
            onKeyDown={(e) => e.key === "Enter" && create()}
            className="flex-1 bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 h-10 text-sm"
          />
          <button onClick={create} className="btn-gold" data-testid="collab-create">Create link</button>
        </div>
      </div>

      {loading ? (
        <div className="text-[#8A8A8A]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[#2A2A2A] p-8 text-center text-[#8A8A8A]" data-testid="collab-empty">
          No collaborators yet. Add one above to generate the first referral link.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((c) => (
            <div key={c.id} className="border border-[#2A2A2A] p-4" data-testid={`collab-row-${c.slug}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-[#F5F1EB] font-display text-lg">{c.name}</div>
                  <div className="text-[10px] text-[#8A8A8A] uppercase tracking-[0.18em]">
                    slug: {c.slug} · WhatsApp clicks: {c.whatsappClicks ?? 0}
                    {" · "}
                    <span className={c.active ? "text-[#C9A55A]" : "text-[#8A8A8A]"}>
                      {c.active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleActive(c)}
                    className="text-[11px] uppercase tracking-[0.18em] text-[#F5F1EB] hover:text-[#C9A55A] px-2"
                    data-testid={`collab-toggle-${c.slug}`}
                  >
                    {c.active ? "Deactivate" : "Activate"}
                  </button>
                  <button
                    onClick={() => remove(c)}
                    className="text-[11px] uppercase tracking-[0.18em] text-[#B8342A] hover:opacity-80 inline-flex items-center gap-1"
                    data-testid={`collab-delete-${c.slug}`}
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <input
                  readOnly
                  value={c.url}
                  onFocus={(e) => e.target.select()}
                  data-testid={`collab-url-${c.slug}`}
                  className="flex-1 bg-[#0C0C0C] border border-[#2A2A2A] text-[#C9A55A] px-2 h-9 text-xs font-mono"
                />
                <button
                  onClick={() => copy(c)}
                  className="inline-flex items-center gap-1 px-3 h-9 border border-[#C9A55A]/50 text-[#C9A55A] hover:bg-[#C9A55A] hover:text-[#0C0C0C] text-[11px] uppercase tracking-[0.18em]"
                  data-testid={`collab-copy-${c.slug}`}
                >
                  {copied === c.id ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy link</>}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
